import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { contact } from "../mock";
import { useLang } from "../i18n";

/** Offizielles WhatsApp-Glyph als Inline-SVG (keine Emojis). */
export function WhatsAppIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.966 1.164-.198.199-.396.223-.693.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.172-.297-.018-.458.13-.606.136-.135.297-.355.446-.532.149-.179.198-.306.298-.504.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.172-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

export function waLink(text) {
  return `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(text)}`;
}

/** Schwebender WhatsApp-Knopf – öffnet direkt den Chat mit der Pension. */
export function FloatingWhatsApp() {
  const { t } = useLang();
  const w = t.whatsapp;
  const [visible, setVisible] = useState(false);
  const [hint, setHint] = useState(true);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 420);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={`fixed bottom-5 right-5 z-40 flex items-end gap-3 transition-all duration-500 sm:bottom-7 sm:right-7 ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
      }`}
    >
      {hint && (
        <div className="hidden items-center gap-3 rounded-2xl border border-slate-200 bg-white py-3 pl-4 pr-2 shadow-[0_18px_40px_-20px_rgba(42,43,47,0.45)] sm:flex">
          <span className="max-w-[200px] text-[13px] leading-snug text-slate-600">{w.hint}</span>
          <button
            type="button"
            onClick={() => setHint(false)}
            aria-label={w.hintClose}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <a
        href={waLink(w.prefill)}
        target="_blank"
        rel="noreferrer"
        aria-label={w.aria}
        className="group relative grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-white shadow-[0_16px_34px_-14px_rgba(37,211,102,0.8)] transition-transform duration-300 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#128C7E]"
      >
        <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366]/35" aria-hidden="true" />
        <WhatsAppIcon className="relative h-7 w-7" />
      </a>
    </div>
  );
}

/** Breiter WhatsApp-Knopf für Kontaktbereich / Menü. */
export function WhatsAppButton({ className = "", full = false }) {
  const { t } = useLang();
  const w = t.whatsapp;
  return (
    <a
      href={waLink(w.prefill)}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1ebe5b] hover:shadow-[0_16px_30px_-16px_rgba(37,211,102,0.9)] ${
        full ? "w-full" : ""
      } ${className}`}
    >
      <WhatsAppIcon className="h-4 w-4" /> {w.cta}
    </a>
  );
}
